import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    BarChart3,
    Database,
    Radar,
    Sparkles,
    Plug,
    Microscope,
    FileText,
    Check,
    X,
    Linkedin,
    Mail,
    MapPin,
    ShoppingBag,
    Briefcase,
    Hash,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import CalendlyEmbed from "@/components/CalendlyEmbed";
import FallbackForm from "@/components/FallbackForm";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import CookieBanner from "@/components/CookieBanner";

/* ====== INSERT POINT: PLAUSIBLE EVENT WRAPPER ======
   The page calls window.plausible(eventName) if the Plausible script is loaded
   via /public/index.html. Otherwise the calls are silent no-ops. */
const track = (name, props) => {
    if (typeof window !== "undefined" && window.plausible) {
        window.plausible(name, props ? { props } : undefined);
    }
};

/* ---------- Sticky header ---------- */
const Header = () => (
    <header
        className="sticky top-0 z-30 w-full bg-white/75 backdrop-blur-md border-b border-border"
        data-testid="site-header"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link
                to="/"
                className="flex items-center gap-2"
                data-testid="header-logo"
            >
                <span className="inline-flex w-7 h-7 rounded-md bg-primary text-primary-foreground items-center justify-center font-bold text-sm">
                    G
                </span>
                <span className="font-semibold tracking-tight text-[15px]">
                    GrowthX <span className="text-slate-500">AI</span>
                </span>
            </Link>
            <a
                href="#book-call"
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 font-medium text-sm transition-colors"
                data-testid="header-book-btn"
            >
                Book audit call
                <ArrowRight className="w-3.5 h-3.5" />
            </a>
        </div>
    </header>
);

/* ---------- Hero ---------- */
const Hero = () => {
    const onPathClick = (vertical) => {
        track("hero_path_selected", { vertical });
        // scroll to the problem section to begin reading
        const el = document.getElementById("problem");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section
            id="hero"
            className="relative overflow-hidden border-b border-border"
            data-testid="hero-section"
        >
            <div className="absolute inset-0 grid-pattern pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-20 md:pb-28">
                <div className="max-w-4xl">
                    <div
                        className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full border border-border bg-white text-slate-600 mb-6"
                        data-testid="hero-eyebrow"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        3–5 Actions Every Monday
                    </div>
                    <h1
                        className="text-[44px] sm:text-6xl lg:text-[80px] font-bold tracking-tighter leading-[1.04] text-slate-900"
                        data-testid="hero-headline"
                    >
                        Know What to Fix in Your GTM.
                        <span className="block text-primary">Every Week.</span>
                    </h1>
                    <p
                        className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl"
                        data-testid="hero-subheadline"
                    >
                        We audit your ads, funnel, and CRM — then send specific
                        actions every Monday.{" "}
                        <span className="text-slate-900 font-medium">
                            Not dashboards. Decisions.
                        </span>
                    </p>

                    <div
                        className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl"
                        data-testid="hero-path-buttons"
                    >
                        <button
                            type="button"
                            onClick={() => onPathClick("ecommerce")}
                            className="group inline-flex items-center justify-between gap-3 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-5 py-4 font-medium text-base text-left transition-all min-h-[44px]"
                            data-testid="hero-ecommerce-btn"
                        >
                            <span className="flex items-center gap-2.5">
                                <ShoppingBag className="w-5 h-5" />
                                I run an ecommerce brand
                            </span>
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </button>
                        <button
                            type="button"
                            onClick={() => onPathClick("b2b")}
                            className="group inline-flex items-center justify-between gap-3 bg-white text-primary border border-primary hover:bg-primary/5 rounded-lg px-5 py-4 font-medium text-base text-left transition-all min-h-[44px]"
                            data-testid="hero-b2b-btn"
                        >
                            <span className="flex items-center gap-2.5">
                                <Briefcase className="w-5 h-5" />
                                I run a B2B / lead-gen business
                            </span>
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </button>
                    </div>

                    <div
                        className="mt-10 flex flex-col gap-4"
                        data-testid="hero-social-proof"
                    >
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                            Trusted by founders in AU · IN · US
                        </p>
                    </div>
                </div>

                {/* Slack brief mockup tease */}
                <div className="mt-16 md:mt-20 max-w-3xl">
                    <SlackTeaseCard />
                </div>
            </div>
        </section>
    );
};

const SlackTeaseCard = () => (
    <div
        className="rounded-2xl border border-border bg-white shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)] overflow-hidden"
        data-testid="hero-slack-tease"
    >
        <div className="px-5 py-3 bg-slate-50 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                </div>
                <span className="text-xs font-mono text-slate-500 ml-2">
                    #gtm-actions
                </span>
            </div>
            <span className="text-xs font-mono text-slate-400">
                Mon · 8:00 AM
            </span>
        </div>
        <div className="p-5 md:p-6 flex gap-4">
            <div className="shrink-0 w-9 h-9 rounded-md bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center">
                Gx
            </div>
            <div className="flex-1">
                <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-slate-900">
                        GrowthX
                    </span>
                    <span className="text-xs text-slate-400">APP</span>
                    <span className="text-xs text-slate-400">8:00 AM</span>
                </div>
                <p className="mt-1 text-slate-700">
                    Week 3 — 5 actions queued. Top one:{" "}
                    <span className="font-semibold text-slate-900">
                        Pause &ldquo;project management software free&rdquo;
                    </span>{" "}
                    keyword group. $2,400/mo wasted, 0 conversions.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-100">
                        ADS
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100">
                        Save $2,400/mo
                    </span>
                </div>
            </div>
        </div>
    </div>
);

/* ---------- Section title ---------- */
const SectionTitle = ({ overline, title, subtitle, testId }) => (
    <div className="max-w-3xl" data-testid={testId}>
        {overline && (
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
                // {overline}
            </p>
        )}
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] text-slate-900">
            {title}
        </h2>
        {subtitle && (
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                {subtitle}
            </p>
        )}
    </div>
);

/* ---------- Problem grid ---------- */
const PROBLEMS = [
    {
        icon: BarChart3,
        title: "Ads Show Data, Not Decisions",
        body: "Google and Meta give you numbers. They don't tell you which campaign to pause, where to shift budget, or what headline to test.",
    },
    {
        icon: Database,
        title: "CRM Shows Leads, Not Why",
        body: "You know leads came in. You don't know why they converted or dropped. Attribution is a mess. Pipeline feels random.",
    },
    {
        icon: Radar,
        title: "Market Signals Are Scattered",
        body: "Customers are talking on Reddit, G2, and forums. Competitors are changing pricing. You find out days later — or never.",
    },
    {
        icon: Sparkles,
        title: "AI Is Invisible to You",
        body: "ChatGPT, Perplexity, and Gemini are recommending products in your category. You don't know if they're recommending you or your competitor.",
    },
];

const ProblemGrid = () => (
    <section
        id="problem"
        className="py-24 md:py-32 border-b border-border"
        data-testid="problem-section"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
                overline="The Problem"
                title="Most founders don't know what to fix next."
                subtitle="Four signals live in four tools. Nobody connects them. So decisions get made on vibes."
                testId="problem-title"
            />
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {PROBLEMS.map((p) => (
                    <div
                        key={p.title}
                        className="rounded-xl border border-border bg-white p-7 md:p-8 transition-all hover:border-primary/30 hover:-translate-y-0.5 hover:shadow-sm"
                        data-testid={`problem-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                        <div className="inline-flex w-10 h-10 rounded-lg bg-primary/5 text-primary items-center justify-center mb-5">
                            <p.icon className="w-5 h-5" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900">
                            {p.title}
                        </h3>
                        <p className="mt-3 text-slate-600 leading-relaxed">
                            {p.body}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* ---------- How it works ---------- */
const STEPS = [
    {
        icon: Plug,
        n: "01",
        title: "Connect",
        body: "Read-only access to Google Ads, Meta, LinkedIn, Shopify/WooCommerce, CRM, GA4. Or send exports if access is high-friction. We send a structured intake doc.",
    },
    {
        icon: Microscope,
        n: "02",
        title: "Audit",
        body: "280+ decision rules across 15+ specialised modules. Every finding backed by a first principle — not a generic checklist. 5–7 business days.",
    },
    {
        icon: FileText,
        n: "03",
        title: "Brief",
        body: "A PDF (15–25 pages): top 3 critical issues, top 3 growth opportunities, 90-day roadmap, $ impact on every finding. Then weekly Monday briefs.",
    },
];

const HowItWorks = () => (
    <section
        id="how-it-works"
        className="py-24 md:py-32 border-b border-border bg-slate-50/50"
        data-testid="how-section"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
                overline="How It Works"
                title="From access to actions in under a week."
                testId="how-title"
            />
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                {STEPS.map((s, i) => (
                    <div
                        key={s.n}
                        className="relative rounded-xl bg-white border border-border p-8"
                        data-testid={`how-step-${i + 1}`}
                    >
                        <span className="font-mono text-xs text-slate-400">
                            {s.n}
                        </span>
                        <div className="mt-3 inline-flex w-10 h-10 rounded-lg bg-primary text-primary-foreground items-center justify-center">
                            <s.icon className="w-5 h-5" />
                        </div>
                        <h3 className="mt-5 text-2xl font-semibold tracking-tight text-slate-900">
                            {s.title}
                        </h3>
                        <p className="mt-2 text-slate-600 leading-relaxed">
                            {s.body}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* ---------- Sample report (Slack brief) ---------- */
const ACTIONS = [
    {
        n: 1,
        title: "Implement Google Ads Enhanced Conversions for Leads",
        body: "CRM shows 40% of converted leads aren't firing back to Google. Smart Bidding is optimising on incomplete data.",
        impact: "$3,200/mo efficiency gain",
        tag: "ADS",
    },
    {
        n: 2,
        title: "Pause keyword group \u201Cproject management software free\u201D",
        body: "$2,400 spent this month, 0 conversions, all traffic bounces in 8 seconds.",
        impact: "Save $2,400/mo",
        tag: "ADS",
    },
    {
        n: 3,
        title: "Fix Meta Pixel Purchase event on checkout",
        body: "31% attribution gap between Meta-reported and actual orders. EMQ score 4.4/10, no CAPI.",
        impact: "Recover attribution on ~$18K/mo spend",
        tag: "FUNNEL",
    },
    {
        n: 4,
        title: "Add abandoned-cart flow in Klaviyo",
        body: "68% add-to-cart-to-checkout drop-off, no recovery automation live.",
        impact: "$4,100/mo recovered revenue",
        tag: "CRM",
    },
];

const TAG_STYLES = {
    ADS: "bg-blue-50 text-blue-700 border-blue-100",
    CRM: "bg-purple-50 text-purple-700 border-purple-100",
    FUNNEL: "bg-amber-50 text-amber-800 border-amber-100",
    SIGNAL: "bg-emerald-50 text-emerald-700 border-emerald-100",
};

const SampleReport = () => (
    <section
        id="sample-report"
        className="py-24 md:py-32 border-b border-border"
        data-testid="sample-report-section"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
                overline="What You Actually Get"
                title="A real weekly brief looks like this."
                subtitle="Every Monday. Specific. Quantified. Prioritised."
                testId="sample-title"
            />

            <div
                className="mt-14 rounded-2xl border border-border slack-card shadow-[0_30px_80px_-40px_rgba(15,23,42,0.25)] overflow-hidden"
                data-testid="sample-report-card"
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-slate-50">
                    <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-slate-500">
                            #gtm-actions
                        </span>
                        <span className="hidden sm:inline text-xs text-slate-400">
                            ·
                        </span>
                        <span className="hidden sm:inline text-xs text-slate-500">
                            Monday brief
                        </span>
                    </div>
                    <span className="font-mono text-xs text-slate-400">
                        Week 3 · 8:00 AM
                    </span>
                </div>

                <div className="p-5 md:p-8">
                    <div className="flex items-start gap-4 mb-7">
                        <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center shrink-0">
                            Gx
                        </div>
                        <div>
                            <div className="flex items-baseline gap-2">
                                <span className="font-semibold text-slate-900">
                                    GrowthX
                                </span>
                                <span className="text-xs text-slate-400">
                                    APP
                                </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                                Week 3 — GTM Actions. 4 of 5 below.
                            </p>
                        </div>
                    </div>

                    <ul className="space-y-5">
                        {ACTIONS.map((a) => (
                            <li
                                key={a.n}
                                className="rounded-xl border border-border bg-white p-5 md:p-6"
                                data-testid={`sample-action-${a.n}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="shrink-0 w-8 h-8 rounded-md bg-slate-100 text-slate-700 font-semibold text-sm flex items-center justify-center font-mono">
                                        {a.n}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-slate-900 text-base md:text-[17px] leading-snug">
                                            {a.title}
                                        </h4>
                                        <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">
                                            {a.body}
                                        </p>
                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider border ${TAG_STYLES[a.tag]}`}
                                            >
                                                {a.tag}
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-md">
                                                <Hash className="w-3 h-3" />
                                                Expected impact: {a.impact}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    </section>
);

/* ---------- Methodology moat ---------- */
const MODULES = [
    {
        key: "meta",
        label: "Meta Ads",
        rules: 30,
        principles: 18,
        note: "Andromeda 2026 extensions, CAPI, EMQ scoring",
    },
    {
        key: "google",
        label: "Google Ads",
        rules: 42,
        principles: 22,
        note: "Smart Bidding, Enhanced Conversions, GA4 events",
    },
    {
        key: "crm",
        label: "CRM",
        rules: 26,
        principles: 14,
        note: "Lifecycle stages, attribution, signal back-fire",
    },
    {
        key: "shopify",
        label: "Shopify",
        rules: 24,
        principles: 12,
        note: "Checkout, pixel coverage, post-purchase flows",
    },
    {
        key: "klaviyo",
        label: "Klaviyo",
        rules: 18,
        principles: 9,
        note: "Cart, browse, win-back, deliverability",
    },
    {
        key: "ga4",
        label: "GA4",
        rules: 22,
        principles: 11,
        note: "Event taxonomy, channel grouping, consent mode",
    },
];

const MethodologyMoat = () => {
    const [active, setActive] = useState(MODULES[0]);
    useEffect(() => {
        track("scroll_to_methodology");
    }, []);
    return (
        <section
            id="methodology"
            className="py-24 md:py-32 border-b border-border bg-primary text-primary-foreground"
            data-testid="methodology-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60 mb-4">
                    // Methodology
                </p>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] max-w-3xl">
                    Built on first principles, not best practices.
                </h2>
                <p className="mt-5 text-lg text-white/75 leading-relaxed max-w-3xl">
                    Most audit tools are checklists. GrowthX is a rule library:
                    280+ decision rules across 15 source skills, each citing a
                    first principle from how the platforms actually work in
                    2026. Every finding traces back to a principle. Every
                    principle has kill-switches that prevent false positives.
                    The typical audit produces 3–7 real findings — not 30 noisy
                    ones.
                </p>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div
                        className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3"
                        data-testid="methodology-modules"
                    >
                        {MODULES.map((m) => {
                            const isActive = active.key === m.key;
                            return (
                                <button
                                    key={m.key}
                                    type="button"
                                    onMouseEnter={() => setActive(m)}
                                    onFocus={() => setActive(m)}
                                    onClick={() => setActive(m)}
                                    className={`text-left rounded-lg p-4 border transition-all min-h-[44px] ${
                                        isActive
                                            ? "bg-white text-slate-900 border-white"
                                            : "bg-white/5 text-white border-white/10 hover:bg-white/10"
                                    }`}
                                    data-testid={`methodology-module-${m.key}`}
                                >
                                    <span className="block font-semibold text-[15px]">
                                        {m.label}
                                    </span>
                                    <span
                                        className={`block mt-1 text-xs font-mono ${isActive ? "text-slate-500" : "text-white/50"}`}
                                    >
                                        {m.rules} rules · {m.principles}{" "}
                                        principles
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div
                        className="lg:col-span-5 rounded-xl border border-white/10 bg-white/[0.04] p-6 md:p-8"
                        data-testid="methodology-detail"
                    >
                        <span className="text-xs font-mono uppercase tracking-widest text-white/50">
                            Currently viewing
                        </span>
                        <h3 className="mt-2 text-2xl font-semibold tracking-tight">
                            {active.label}
                        </h3>
                        <div className="mt-5 grid grid-cols-2 gap-4">
                            <div>
                                <span className="block text-3xl font-bold tracking-tight">
                                    {active.rules}
                                </span>
                                <span className="text-xs text-white/60 uppercase tracking-wider">
                                    Decision rules
                                </span>
                            </div>
                            <div>
                                <span className="block text-3xl font-bold tracking-tight">
                                    {active.principles}
                                </span>
                                <span className="text-xs text-white/60 uppercase tracking-wider">
                                    First principles
                                </span>
                            </div>
                        </div>
                        <p className="mt-5 text-sm text-white/75 leading-relaxed">
                            {active.note}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ---------- Case study ---------- */
const CaseStudy = () => (
    <section
        id="case-study"
        className="py-24 md:py-32 border-b border-border"
        data-testid="case-study-section"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
                overline="Case Study"
                title="What an audit unlocks."
                subtitle="Anonymised. Numbers are real."
                testId="case-title"
            />

            <div className="mt-14 rounded-2xl border border-border overflow-hidden grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 md:p-12 bg-primary text-primary-foreground">
                    <span className="text-xs font-mono uppercase tracking-widest text-white/60">
                        Client
                    </span>
                    <h3 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">
                        AU supplements brand, $300K/yr ARR
                    </h3>

                    <div className="mt-8 space-y-6">
                        <div>
                            <span className="text-xs font-mono uppercase tracking-widest text-white/60">
                                Starting state
                            </span>
                            <p className="mt-1.5 text-white/90 leading-relaxed">
                                Meta Ads at{" "}
                                <span className="font-semibold text-white">
                                    $14.65 CPA
                                </span>{" "}
                                against $15 target — but{" "}
                                <span className="font-semibold text-white">
                                    29 of 60 days
                                </span>{" "}
                                closed over target. Volatile.
                            </p>
                        </div>
                        <div>
                            <span className="text-xs font-mono uppercase tracking-widest text-white/60">
                                What we found
                            </span>
                            <p className="mt-1.5 text-white/90 leading-relaxed">
                                3 critical signal issues — EMQ{" "}
                                <span className="font-semibold text-white">
                                    4.4 / 10
                                </span>
                                , no CAPI, attribution gap{" "}
                                <span className="font-semibold text-white">
                                    31%
                                </span>
                                .
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-8 md:p-12 bg-white">
                    <span className="text-xs font-mono uppercase tracking-widest text-slate-400">
                        Result
                    </span>
                    <h3 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                        After foundation fixes
                    </h3>
                    <ul className="mt-6 space-y-4 text-slate-700">
                        {[
                            "CPA stabilised within target.",
                            "Attribution reconciliation closed.",
                            "Scaling unlocked — budget increased without breaking CPA.",
                        ].map((t) => (
                            <li key={t} className="flex items-start gap-3">
                                <Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                                <span className="leading-relaxed">{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    </section>
);

/* ---------- Founder ---------- */
const Founder = () => (
    <section
        id="founder"
        className="py-24 md:py-32 border-b border-border bg-slate-50/50"
        data-testid="founder-section"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-4">
                <div
                    className="aspect-[4/5] rounded-2xl overflow-hidden border border-border bg-slate-100"
                    data-testid="founder-photo"
                >
                    <img
                        src="https://customer-assets.emergentagent.com/job_growthx-audit/artifacts/2jh5j6b4_linkedin-profile-image.jpeg"
                        alt="Sandeep — Founder, GrowthX AI"
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                </div>
                <a
                    href="https://www.linkedin.com/in/v-v-s-sandeep-kumar-530975126/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-primary transition-colors"
                    data-testid="founder-linkedin"
                >
                    <Linkedin className="w-4 h-4" />
                    Connect on LinkedIn
                </a>
            </div>
            <div className="lg:col-span-8">
                <SectionTitle
                    overline="Who's Building This"
                    title="Built by someone who's operated and rebuilt GTM systems."
                    testId="founder-title"
                />
                <div className="mt-7 space-y-5 text-slate-700 leading-relaxed text-[17px]">
                    <p>
                        I work at the intersection of engineering, marketing,
                        and revenue. Started as a software engineer. Moved into
                        performance marketing. Over the last 7+ years, I&apos;ve
                        built and scaled acquisition systems across SaaS,
                        healthcare, and D2C — managing{" "}
                        <span className="font-semibold text-slate-900">
                            $1M+ in ad spend across Australia, India, and the
                            US.
                        </span>
                    </p>
                    <p>
                        GTM is fragmented — ads, CRM, landing pages, and
                        analytics operate in silos. Teams optimise channels,
                        not outcomes. That&apos;s what led to GrowthX AI:
                        systems that connect market signals → user intent →
                        revenue actions. Not dashboards. Execution layers that
                        move pipeline.
                    </p>
                    <p>
                        I work with founders who don&apos;t want more tools —
                        they want predictable, compounding growth.
                    </p>
                </div>
                <p className="mt-7 text-sm font-semibold text-slate-900">
                    Sandeep · AI GTM Engineer · Founder, GrowthX AI
                </p>
            </div>
        </div>
    </section>
);

/* ---------- Comparison table ---------- */
const COMPARE_ROWS = [
    {
        label: "What it does",
        cells: [
            "SEO + ad data",
            "CRM + automation",
            "Pulls ad data into sheets",
            "Combines all signals into weekly actions",
        ],
    },
    {
        label: "Output",
        cells: [
            "Dashboards + rankings",
            "Pipeline + reports",
            "Raw data export",
            "“Do these 3–5 things this week”",
        ],
    },
    {
        label: "Community signals",
        cells: [false, false, false, "Reddit, forums, G2, reviews"],
    },
    {
        label: "AI / LLM visibility",
        cells: ["New (basic)", false, false, "ChatGPT, Perplexity, Gemini audit"],
    },
    {
        label: "Ad account analysis",
        cells: [
            "Basic PPC data",
            "Attribution only",
            "Data, no analysis",
            "Full audit + fix recommendations",
        ],
    },
    {
        label: "Cross-signal synthesis",
        cells: [false, false, false, "Market + ads + funnel + AI combined"],
    },
    {
        label: "Who does the thinking",
        cells: ["You", "You", "You", "We do. You decide what to act on."],
        highlight: true,
    },
];

const Comparison = () => (
    <section
        id="comparison"
        className="py-24 md:py-32 border-b border-border"
        data-testid="comparison-section"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle
                overline="How We're Different"
                title="Not another tool. The analyst who reads all your tools."
                testId="comparison-title"
            />

            <div
                className="mt-14 rounded-2xl border border-border overflow-hidden bg-white"
                data-testid="comparison-table"
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-left min-w-[760px]">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="p-5 md:p-6 text-xs font-bold uppercase tracking-widest text-slate-500"></th>
                                <th className="p-5 md:p-6 font-semibold text-slate-700 text-sm">
                                    SEMrush / Ahrefs
                                </th>
                                <th className="p-5 md:p-6 font-semibold text-slate-700 text-sm">
                                    HubSpot / Salesforce
                                </th>
                                <th className="p-5 md:p-6 font-semibold text-slate-700 text-sm">
                                    Supermetrics
                                </th>
                                <th className="p-5 md:p-6 font-semibold text-primary text-sm bg-primary/5 border-l border-primary/20 relative">
                                    GrowthX AI
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary text-primary-foreground">
                                        Us
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARE_ROWS.map((row, i) => (
                                <tr
                                    key={row.label}
                                    className={`border-b border-border last:border-b-0 ${row.highlight ? "bg-slate-50" : ""}`}
                                >
                                    <td className="p-5 md:p-6 font-medium text-slate-900 text-sm align-top">
                                        {row.label}
                                    </td>
                                    {row.cells.map((c, idx) => {
                                        const isLast = idx === 3;
                                        return (
                                            <td
                                                key={idx}
                                                className={`p-5 md:p-6 text-sm align-top ${
                                                    isLast
                                                        ? "bg-primary/5 border-l border-primary/20 text-slate-900 font-medium"
                                                        : "text-slate-600"
                                                }`}
                                                data-testid={`compare-row-${i}-col-${idx}`}
                                            >
                                                {c === false ? (
                                                    <X className="w-4 h-4 text-slate-300" />
                                                ) : (
                                                    c
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </section>
);

/* ---------- FAQ ---------- */
const FAQS = [
    {
        q: "How much does this cost?",
        a: "Depends on your access tier and account complexity. Book the call and we'll scope it with you — no commitment.",
    },
    {
        q: "What if I only want to share Google Ads access initially?",
        a: "That's our Tier 1 — we audit at ~55% depth and unlock the rest as trust builds.",
    },
    {
        q: "How long does the audit take?",
        a: "5–7 business days from intake completion.",
    },
    {
        q: "Are you an agency or a software product?",
        a: "Hybrid. Software runs the audit, Sandeep + team review findings before delivery — you get human-vetted decisions.",
    },
    {
        q: "Do I need to switch ad platforms or change tools?",
        a: "No. We read what you already have.",
    },
    {
        q: "What if my account is too small?",
        a: "Below $5K/month total paid spend, ROI on the audit is harder. Above that, the audit pays for itself.",
    },
    {
        q: "Where are you based?",
        a: "India / Australia / USA. Global delivery.",
    },
];

const FAQ = () => {
    const ref = useRef(null);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    track("scroll_to_faq");
                    obs.disconnect();
                }
            },
            { threshold: 0.2 },
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return (
        <section
            id="faq"
            ref={ref}
            className="py-24 md:py-32 border-b border-border bg-slate-50/50"
            data-testid="faq-section"
        >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionTitle
                    overline="FAQ"
                    title="Questions we hear most."
                    testId="faq-title"
                />
                <div className="mt-12" data-testid="faq-accordion">
                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="border-b border-border"
                                data-testid={`faq-item-${i}`}
                            >
                                <AccordionTrigger className="text-left text-base md:text-lg font-medium text-slate-900 hover:no-underline py-5">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed text-base pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
};

/* ---------- Final CTA + Calendar ---------- */
const FinalCTA = () => (
    <section
        id="book-call"
        className="py-24 md:py-32 border-b border-border"
        data-testid="final-cta-section"
    >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
                    // Book audit call
                </p>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
                    Stop guessing. Start fixing.
                </h2>
                <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                    Book a 30-minute audit conversation. We&apos;ll review what
                    you&apos;d send us and confirm the audit scope before any
                    commitment.
                </p>
            </div>

            <div className="mt-14">
                <CalendlyEmbed testId="main-calendly-widget" />
            </div>

            <p className="mt-4 text-center text-sm text-slate-500">
                Prefer email?{" "}
                <a
                    href="mailto:info@growthx.digital"
                    className="text-primary font-medium underline-offset-2 hover:underline"
                    data-testid="email-fallback-link"
                >
                    info@growthx.digital
                </a>{" "}
                — we respond within 24 hours.
            </p>

            <div className="mt-16 max-w-2xl mx-auto">
                <FallbackForm />
            </div>
        </div>
    </section>
);

/* ---------- Footer ---------- */
const Footer = () => (
    <footer
        className="bg-slate-900 text-slate-300"
        data-testid="site-footer"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex w-7 h-7 rounded-md bg-white text-slate-900 items-center justify-center font-bold text-sm">
                            G
                        </span>
                        <span className="font-semibold tracking-tight text-white">
                            GrowthX AI
                        </span>
                    </div>
                    <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                        Audit your GTM. Know what to fix every Monday.
                    </p>
                    <div className="mt-5 flex items-center gap-2 text-sm text-slate-400">
                        <MapPin className="w-4 h-4" />
                        <span>India · Australia · USA</span>
                    </div>
                </div>

                <div className="md:col-span-3">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">
                        Links
                    </p>
                    <ul className="space-y-3 text-sm">
                        <li>
                            <Link
                                to="/privacy"
                                className="hover:text-white transition-colors"
                                data-testid="footer-privacy"
                            >
                                Privacy
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/terms"
                                className="hover:text-white transition-colors"
                                data-testid="footer-terms"
                            >
                                Terms
                            </Link>
                        </li>
                        <li>
                            <a
                                href="#"
                                onClick={(e) => e.preventDefault()}
                                className="inline-flex items-center gap-2 hover:text-white transition-colors"
                                data-testid="footer-linkedin"
                            >
                                <Linkedin className="w-4 h-4" /> Founder
                                LinkedIn (insert URL)
                            </a>
                        </li>
                        <li>
                            <a
                                href="mailto:info@growthx.digital"
                                className="inline-flex items-center gap-2 hover:text-white transition-colors"
                                data-testid="footer-email"
                            >
                                <Mail className="w-4 h-4" />{" "}
                                info@growthx.digital
                            </a>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">
                        Built with
                    </p>
                    <p className="text-sm text-slate-400 leading-relaxed">
                        Anthropic Claude · Google Cloud · Supabase · n8n ·
                        Python · Next.js
                    </p>
                </div>
            </div>

            <div className="mt-14 pt-6 border-t border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                    © {new Date().getFullYear()} GrowthX AI Partners LLP. All
                    rights reserved.
                </p>
                <p className="text-xs text-slate-500">
                    Markets: 🇮🇳 India · 🇦🇺 Australia · 🇺🇸 USA
                </p>
            </div>
        </div>
    </footer>
);

/* ---------- Page ---------- */
export default function LandingPage() {
    return (
        <div className="min-h-screen bg-white text-slate-900 pb-20 md:pb-0">
            <Header />
            <Hero />
            <ProblemGrid />
            <HowItWorks />
            <SampleReport />
            <MethodologyMoat />
            <CaseStudy />
            <Founder />
            <Comparison />
            <FAQ />
            <FinalCTA />
            <Footer />
            <MobileStickyCTA />
            <CookieBanner />
        </div>
    );
}
